import type {
  AttributeType,
  CatalogVersionSource,
  FailoverPolicy,
  CommercialAvailability,
  MediaOwnerType,
  ProductBarcodeSource,
  ProductBarcodeType,
  ProductCardStatus,
  PricingMode,
  RuleType,
  SortOption,
} from './types';
import type {
  PublicationCatalogLifecycleKind,
  PublicationCatalogStatus,
  PublicationChannelKind,
  PublicationCriteriaKind,
  PublicationProfileFieldKind,
  PublicationProfileSource,
  PublicationRuleDirection,
  PublicationRunOutcomeKind,
  PublicationRunStatus,
} from './publicationTypes';

// NOTE: every `label` below is an i18next KEY (e.g. 'catalog:options.stockStatus.inStock'),
// not literal display text — consumers must render `t(option.label)`. This keeps the existing
// lookup ergonomic (still a plain object/array, easy to `.map()`) while deferring the actual
// string to render time, so it follows the active language. Translations live in
// public/locales/{lng}/catalog.json under the `options` key.

export interface SelectOption {
  value: string;
  /** i18next key — render with `t(label)`, not as literal text. */
  label: string;
}

/** Sort presets for the product list/search toolbar. */
export interface ProductSortPreset {
  value: string;
  /** i18next key — render with `t(label)`. */
  label: string;
  sort: SortOption[];
}

export const PRODUCT_SORT_PRESETS: ProductSortPreset[] = [
  { value: 'name-asc', label: 'catalog:options.sort.nameAsc', sort: [{ property: 'name', direction: 'ASC' }] },
  { value: 'name-desc', label: 'catalog:options.sort.nameDesc', sort: [{ property: 'name', direction: 'DESC' }] },
  { value: 'price-asc', label: 'catalog:options.sort.priceAsc', sort: [{ property: 'price', direction: 'ASC' }] },
  { value: 'price-desc', label: 'catalog:options.sort.priceDesc', sort: [{ property: 'price', direction: 'DESC' }] },
  { value: 'newest', label: 'catalog:options.sort.newest', sort: [{ property: 'createdAt', direction: 'DESC' }] },
];

export const STOCK_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'catalog:options.stockFilter.all' },
  { value: 'IN_STOCK', label: 'catalog:options.stockFilter.inStock' },
  { value: 'OUT_OF_STOCK', label: 'catalog:options.stockFilter.outOfStock' },
  { value: 'CHECK_AVAILABILITY', label: 'catalog:options.stockFilter.checkAvailability' },
];

/** Availability select (3 states) used in the product create/edit and quick-stock forms. */
export const STOCK_STATUS_OPTIONS: SelectOption[] = [
  { value: 'IN_STOCK', label: 'catalog:options.stockStatus.inStock' },
  { value: 'OUT_OF_STOCK', label: 'catalog:options.stockStatus.outOfStock' },
  { value: 'CHECK_AVAILABILITY', label: 'catalog:options.stockStatus.checkAvailability' },
];

/** i18next keys — render with `t(STOCK_STATUS_LABELS[status])`. */
export const STOCK_STATUS_LABELS: Record<string, string> = {
  IN_STOCK: 'catalog:options.stockStatus.inStock',
  OUT_OF_STOCK: 'catalog:options.stockStatus.outOfStock',
  CHECK_AVAILABILITY: 'catalog:options.stockStatus.checkAvailability',
};

export const PRODUCT_CARD_STATUS_OPTIONS: { value: ProductCardStatus; label: string }[] = [
  { value: 'DRAFT', label: 'catalog:options.cardStatus.draftLong' },
  { value: 'ACTIVE', label: 'catalog:options.cardStatus.activeLong' },
  { value: 'ARCHIVED', label: 'catalog:options.cardStatus.archivedLong' },
];

/** i18next keys — render with `t(PRODUCT_CARD_STATUS_LABELS[status])`. */
export const PRODUCT_CARD_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'catalog:options.cardStatus.draft',
  ACTIVE: 'catalog:options.cardStatus.active',
  ARCHIVED: 'catalog:options.cardStatus.archived',
};

// NOTE: 'ARCHIVED' is intentionally excluded from this list. Archiving is a master-data
// lifecycle action surfaced from ProductPublicationCard, while channel publication itself is
// owned by the publication workspace's catalog bindings and rules.
export const COMMERCIAL_AVAILABILITY_OPTIONS: { value: CommercialAvailability; label: string }[] = [
  { value: 'AVAILABLE', label: 'catalog:options.commercialAvailability.available' },
  { value: 'CHECK_AVAILABILITY', label: 'catalog:options.commercialAvailability.checkAvailability' },
  { value: 'PREORDER', label: 'catalog:options.commercialAvailability.preorder' },
  { value: 'COMING_SOON', label: 'catalog:options.commercialAvailability.comingSoon' },
  { value: 'OUT_OF_STOCK', label: 'catalog:options.commercialAvailability.outOfStock' },
  { value: 'TEMPORARILY_UNAVAILABLE', label: 'catalog:options.commercialAvailability.temporarilyUnavailable' },
];

/** i18next keys — render with `t(COMMERCIAL_AVAILABILITY_LABELS[status])`. */
export const COMMERCIAL_AVAILABILITY_LABELS: Record<string, string> = Object.fromEntries(
  COMMERCIAL_AVAILABILITY_OPTIONS.map((o) => [o.value, o.label]),
);

export function productCardStatusColor(status?: string): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'DRAFT':
      return 'warning';
    default:
      return 'default';
  }
}

export function commercialAvailabilityColor(
  status?: string,
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'AVAILABLE':
    case 'PREORDER':
      return 'success';
    // CHECK_AVAILABILITY = buyer uncertainty, not a positive state
    case 'CHECK_AVAILABILITY':
    case 'COMING_SOON':
      return 'warning';
    case 'OUT_OF_STOCK':
    case 'TEMPORARILY_UNAVAILABLE':
      return 'error';
    default:
      return 'default';
  }
}

/** Colour bucket for the availability chip. OUT_OF_STOCK is error (requires restocking action). */
export function stockStatusColor(status?: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'IN_STOCK':
      return 'success';
    case 'CHECK_AVAILABILITY':
      return 'warning';
    case 'OUT_OF_STOCK':
      return 'error';
    default:
      return 'default';
  }
}

/** i18next keys — render with `t(PRICE_STATUS_LABELS[status])`. */
export const PRICE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'catalog:options.priceStatus.active',
  NO_PRICE: 'catalog:options.priceStatus.noPrice',
  PENDING: 'catalog:options.priceStatus.pending',
  BLOCKED: 'catalog:options.priceStatus.blocked',
  EXPIRED: 'catalog:options.priceStatus.expired',
};

/** A muted/positive/etc. colour bucket for a price status chip. */
export function priceStatusColor(status?: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'BLOCKED':
    case 'EXPIRED':
      return 'error';
    default:
      return 'default';
  }
}

// ── Price lists (adr-pricing-025, plan-platform-002 WP4.2) ───────────────────
/** i18next keys — `STATIC` is the only kind with an execution path in v1 (design spec §15). */
export const PRICE_LIST_KIND_LABELS: Record<string, string> = {
  STATIC: 'catalog:priceListsPanel.kind.static',
  ADJUSTMENT: 'catalog:priceListsPanel.kind.adjustment',
};

// ── Bulk repricing batches (epic-pricing-002 Phase 1, plan-platform-002 WP4.3) ──────────────
/** i18next keys — render with `t(BATCH_STATUS_LABELS[status] ?? status)` (enum-label-fallback
 * tolerance: `web-price-engine-service` versions `BatchStatus` independently of this build). */
export const BATCH_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'catalog:bulkBatchesPanel.status.draft',
  DRY_RUN_READY: 'catalog:bulkBatchesPanel.status.dryRunReady',
  SCHEDULED: 'catalog:bulkBatchesPanel.status.scheduled',
  APPLYING: 'catalog:bulkBatchesPanel.status.applying',
  APPLIED: 'catalog:bulkBatchesPanel.status.applied',
  FAILED: 'catalog:bulkBatchesPanel.status.failed',
  CANCELLED: 'catalog:bulkBatchesPanel.status.cancelled',
};

export function batchStatusColor(
  status?: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'APPLIED':
      return 'success';
    case 'DRY_RUN_READY':
    case 'SCHEDULED':
    case 'APPLYING':
      return 'info';
    case 'FAILED':
      return 'error';
    default:
      return 'default';
  }
}

/** i18next keys — render with `t(BATCH_ITEM_OUTCOME_LABELS[outcome] ?? outcome)`. */
export const BATCH_ITEM_OUTCOME_LABELS: Record<string, string> = {
  OK: 'catalog:bulkBatchesPanel.outcome.ok',
  BLOCKED: 'catalog:bulkBatchesPanel.outcome.blocked',
  REQUIRES_APPROVAL: 'catalog:bulkBatchesPanel.outcome.approval',
  ERROR: 'catalog:bulkBatchesPanel.outcome.error',
};

export function batchItemOutcomeColor(outcome?: string): 'success' | 'warning' | 'error' | 'default' {
  switch (outcome) {
    case 'OK':
      return 'success';
    case 'REQUIRES_APPROVAL':
      return 'warning';
    case 'BLOCKED':
    case 'ERROR':
      return 'error';
    default:
      return 'default';
  }
}

// ── Markdown plans (epic-pricing-002 Phase 1, plan-platform-002 WP4.3) ──────────────────────
/** i18next keys — render with `t(MARKDOWN_PLAN_STATUS_LABELS[status] ?? status)`. */
export const MARKDOWN_PLAN_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'catalog:markdownPlansPanel.status.draft',
  ACTIVE: 'catalog:markdownPlansPanel.status.active',
  COMPLETED: 'catalog:markdownPlansPanel.status.completed',
  CANCELLED: 'catalog:markdownPlansPanel.status.cancelled',
};

export function markdownPlanStatusColor(status?: string): 'success' | 'default' {
  return status === 'ACTIVE' ? 'success' : 'default';
}

// ── Customer groups (epic-pricing-002 Phase 1, adr-pricing-009, plan-platform-002 WP4.3) ────
/** i18next keys — render with `t(CUSTOMER_GROUP_KIND_LABELS[kind] ?? kind)`. */
export const CUSTOMER_GROUP_KIND_LABELS: Record<string, string> = {
  STATIC: 'catalog:customerGroupsPanel.kind.static',
  DYNAMIC: 'catalog:customerGroupsPanel.kind.dynamic',
};

// ── Rounding policies (adr-pricing-022, epic-pricing-002 Phase 1, plan-platform-002 WP4.3) ──
/** i18next keys — render with `t(ROUNDING_MODE_LABELS[mode] ?? mode)`. */
export const ROUNDING_MODE_LABELS: Record<string, string> = {
  HALF_UP: 'catalog:roundingPanel.mode.halfUp',
  UP: 'catalog:roundingPanel.mode.up',
  DOWN: 'catalog:roundingPanel.mode.down',
};

/** i18next keys — render with `t(ROUNDING_SCOPE_LABELS[scope] ?? scope)`. */
export const ROUNDING_SCOPE_LABELS: Record<string, string> = {
  LIST_PRICE: 'catalog:roundingPanel.scope.listPrice',
  DISCOUNT_RESULT: 'catalog:roundingPanel.scope.discountResult',
};

// ── Product barcodes (epic-002, user-story-merchant-portal-039) ─────────────────────────────
export const PRODUCT_BARCODE_TYPE_OPTIONS: { value: ProductBarcodeType; label: string }[] = [
  { value: 'EAN13', label: 'catalog:options.barcodeType.ean13' },
  { value: 'EAN8', label: 'catalog:options.barcodeType.ean8' },
  { value: 'UPC_A', label: 'catalog:options.barcodeType.upcA' },
  { value: 'GTIN14', label: 'catalog:options.barcodeType.gtin14' },
  { value: 'CODE128', label: 'catalog:options.barcodeType.code128' },
  { value: 'OTHER', label: 'catalog:options.barcodeType.other' },
];

/** i18next keys — render with `t(PRODUCT_BARCODE_TYPE_LABELS[type])`. */
export const PRODUCT_BARCODE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  PRODUCT_BARCODE_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

/** i18next keys — render with `t(PRODUCT_BARCODE_SOURCE_LABELS[source])`. */
export const PRODUCT_BARCODE_SOURCE_LABELS: Record<string, string> = {
  GENERATED: 'catalog:options.barcodeSource.generated',
  MANUAL: 'catalog:options.barcodeSource.manual',
  IMPORT: 'catalog:options.barcodeSource.import',
  GS1_RUS: 'catalog:options.barcodeSource.gs1Rus',
  NATIONAL_CATALOG: 'catalog:options.barcodeSource.nationalCatalog',
  POS_VENDOR: 'catalog:options.barcodeSource.posVendor',
};

/** Colour bucket for the barcode-source chip — GENERATED (this system) vs MANUAL (neutral) vs everything else (external). */
export function productBarcodeSourceColor(
  source?: ProductBarcodeSource | string,
): 'primary' | 'default' | 'secondary' {
  switch (source) {
    case 'GENERATED':
      return 'primary';
    case 'MANUAL':
      return 'default';
    default:
      return 'secondary';
  }
}

// ── Publication / lifecycle (status = colour + label) ────────────────────────
/** i18next keys — render with `t(LIFECYCLE_STATUS_LABELS[status])`. */
export const LIFECYCLE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'catalog:options.lifecycleStatus.active',
  ARCHIVED: 'catalog:options.lifecycleStatus.archived',
  COMING_SOON: 'catalog:options.lifecycleStatus.comingSoon',
  PREORDER: 'catalog:options.lifecycleStatus.preorder',
  TEMPORARILY_UNAVAILABLE: 'catalog:options.lifecycleStatus.temporarilyUnavailable',
};

/** Colour bucket for the lifecycle chip (ARCHIVED → muted, transitional states → warning). */
export function lifecycleStatusColor(status?: string): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'COMING_SOON':
    case 'PREORDER':
    case 'TEMPORARILY_UNAVAILABLE':
      return 'warning';
    default:
      return 'default';
  }
}

/** i18next keys — render with `t(PUBLICATION_STATUS_LABELS[status])`. */
export const PUBLICATION_STATUS_LABELS: Record<string, string> = {
  PUBLISHED: 'catalog:options.publicationStatus.published',
  INCLUDED: 'catalog:options.publicationStatus.included',
  PENDING: 'catalog:options.publicationStatus.pending',
  FAILED_PROFILE: 'catalog:options.publicationStatus.failedProfile',
  NO_PRIMARY_CATALOG: 'catalog:options.publicationStatus.noPrimaryCatalog',
  UNPUBLISHED: 'catalog:options.publicationStatus.unpublished',
};

/** Colour bucket for a per-channel publication chip. */
export function publicationStatusColor(status?: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'INCLUDED':
      return 'info';
    case 'PENDING':
      return 'warning';
    case 'FAILED_PROFILE':
    case 'NO_PRIMARY_CATALOG':
      return 'error';
    default:
      return 'default';
  }
}

export const MEDIA_OWNER_TYPE: MediaOwnerType = 'PRODUCT';

export const PRICING_MODE_OPTIONS: { value: PricingMode; label: string }[] = [
  { value: 'INTERNAL', label: 'catalog:options.pricingMode.internal' },
  { value: 'EXTERNAL', label: 'catalog:options.pricingMode.external' },
  { value: 'HYBRID_INTERNAL_FIRST', label: 'catalog:options.pricingMode.hybridInternalFirst' },
  { value: 'HYBRID_EXTERNAL_FIRST', label: 'catalog:options.pricingMode.hybridExternalFirst' },
];

export const FAILOVER_POLICY_OPTIONS: { value: FailoverPolicy; label: string }[] = [
  { value: 'FALLBACK_TO_INTERNAL', label: 'catalog:options.failoverPolicy.fallbackToInternal' },
  { value: 'RETURN_CACHED', label: 'catalog:options.failoverPolicy.returnCached' },
  { value: 'FAIL_FAST', label: 'catalog:options.failoverPolicy.failFast' },
];

export const RULE_TYPE_OPTIONS: { value: RuleType; label: string }[] = [
  { value: 'PRODUCT_DISCOUNT', label: 'catalog:options.ruleType.productDiscount' },
  { value: 'CUSTOMER_DISCOUNT', label: 'catalog:options.ruleType.customerDiscount' },
  { value: 'PROMO_CODE', label: 'catalog:options.ruleType.promoCode' },
  { value: 'LOYALTY', label: 'catalog:options.ruleType.loyalty' },
  { value: 'DELIVERY_FEE', label: 'catalog:options.ruleType.deliveryFee' },
  { value: 'TAX', label: 'catalog:options.ruleType.tax' },
];

/** i18next keys — render with `t(RULE_TYPE_LABELS[type])`. */
export const RULE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  RULE_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

/** Platform channel labels used by legacy pricing/channel settings surfaces. Publication targets
 * for catalog membership are read from web-catalog-publication-service, not from this static list. */
export const PLATFORM_SALES_CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'TELEGRAM_MINI_APP_SHOP', label: 'catalog:options.platformChannel.telegramMiniAppShop' },
  { value: 'TELEGRAM_BOT', label: 'catalog:options.platformChannel.telegramBot' },
  { value: 'MAX_MINI_APP_SHOP', label: 'catalog:options.platformChannel.maxMiniAppShop' },
  { value: 'MAX_BOT', label: 'catalog:options.platformChannel.maxBot' },
  { value: 'MOBILE_APP', label: 'catalog:options.platformChannel.mobileApp' },
  { value: 'WEBSITE', label: 'catalog:options.platformChannel.website' },
  { value: 'RETAIL', label: 'catalog:options.platformChannel.retail' },
];

/** i18next keys — render with `t(PLATFORM_SALES_CHANNEL_LABELS[code])`. */
export const PLATFORM_SALES_CHANNEL_LABELS: Record<string, string> = Object.fromEntries(
  PLATFORM_SALES_CHANNEL_OPTIONS.map((o) => [o.value, o.label]),
);

// ── Attribute dictionary (epic-catalog-004/005) ─────────────────────────────
export const ATTRIBUTE_TYPE_OPTIONS: { value: AttributeType; label: string }[] = [
  { value: 'TEXT', label: 'catalog:options.attributeType.text' },
  { value: 'NUMBER', label: 'catalog:options.attributeType.number' },
  { value: 'BOOLEAN', label: 'catalog:options.attributeType.boolean' },
  { value: 'SELECT', label: 'catalog:options.attributeType.select' },
  { value: 'MULTISELECT', label: 'catalog:options.attributeType.multiselect' },
  { value: 'DATE', label: 'catalog:options.attributeType.date' },
  { value: 'MEASUREMENT', label: 'catalog:options.attributeType.measurement' },
];

/** i18next keys — render with `t(ATTRIBUTE_TYPE_LABELS[type])`. */
export const ATTRIBUTE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ATTRIBUTE_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

/** SELECT/MULTISELECT get their own options manager; the rest get a type-specific validation editor. */
export function attributeHasOptions(type: AttributeType): boolean {
  return type === 'SELECT' || type === 'MULTISELECT';
}

// ── Catalog version history (epic-catalog-005, user-story-merchant-portal-032) ─────────────
/** i18next keys — render with `t(CATALOG_VERSION_SOURCE_LABELS[source])`. */
export const CATALOG_VERSION_SOURCE_LABELS: Record<CatalogVersionSource, string> = {
  API: 'catalog:options.versionSource.api',
  IMPORT: 'catalog:options.versionSource.import',
  SYSTEM: 'catalog:options.versionSource.system',
};

/** Colour bucket for the version-history source chip. */
export function catalogVersionSourceColor(
  source: CatalogVersionSource,
): 'primary' | 'secondary' | 'default' {
  switch (source) {
    case 'API':
      return 'primary';
    case 'IMPORT':
      return 'secondary';
    default:
      return 'default';
  }
}

// ── Publication workspace (epic-catalog-001) ─────────────────────────────────

export const PUBLICATION_LIFECYCLE_KIND_OPTIONS: { value: PublicationCatalogLifecycleKind; label: string }[] = [
  { value: 'PERMANENT', label: 'catalog:options.publicationLifecycleKind.permanent' },
  { value: 'TEMPORARY', label: 'catalog:options.publicationLifecycleKind.temporary' },
];

/** i18next keys — render with `t(PUBLICATION_LIFECYCLE_KIND_LABELS[kind])`. */
export const PUBLICATION_LIFECYCLE_KIND_LABELS: Record<string, string> = Object.fromEntries(
  PUBLICATION_LIFECYCLE_KIND_OPTIONS.map((o) => [o.value, o.label]),
);

/** i18next keys — render with `t(PUBLICATION_CATALOG_STATUS_LABELS[status])`. */
export const PUBLICATION_CATALOG_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'catalog:options.publicationCatalogStatus.draft',
  ACTIVE: 'catalog:options.publicationCatalogStatus.active',
  PAUSED: 'catalog:options.publicationCatalogStatus.paused',
  EXPIRED: 'catalog:options.publicationCatalogStatus.expired',
  ARCHIVED: 'catalog:options.publicationCatalogStatus.archived',
};

/** Colour bucket for the publication-catalog status chip. */
export function publicationCatalogStatusColor(
  status?: PublicationCatalogStatus | string,
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PAUSED':
      return 'warning';
    case 'EXPIRED':
      return 'error';
    case 'DRAFT':
    case 'ARCHIVED':
    default:
      return 'default';
  }
}

export const PUBLICATION_CHANNEL_KIND_OPTIONS: { value: PublicationChannelKind; label: string }[] = [
  { value: 'STOREFRONT', label: 'catalog:options.publicationChannelKind.storefront' },
  { value: 'RETAIL_POS', label: 'catalog:options.publicationChannelKind.retailPos' },
  { value: 'MARKETPLACE', label: 'catalog:options.publicationChannelKind.marketplace' },
];

/** i18next keys — render with `t(PUBLICATION_CHANNEL_KIND_LABELS[kind])`. */
export const PUBLICATION_CHANNEL_KIND_LABELS: Record<string, string> = Object.fromEntries(
  PUBLICATION_CHANNEL_KIND_OPTIONS.map((o) => [o.value, o.label]),
);

export const PUBLICATION_RULE_DIRECTION_OPTIONS: { value: PublicationRuleDirection; label: string }[] = [
  { value: 'INCLUDE', label: 'catalog:options.publicationRuleDirection.include' },
  { value: 'EXCLUDE', label: 'catalog:options.publicationRuleDirection.exclude' },
];

/** i18next keys — render with `t(PUBLICATION_RULE_DIRECTION_LABELS[direction])`. */
export const PUBLICATION_RULE_DIRECTION_LABELS: Record<string, string> = Object.fromEntries(
  PUBLICATION_RULE_DIRECTION_OPTIONS.map((o) => [o.value, o.label]),
);

/** EXCLUDE always wins a conflict, so it reads as the "stronger" (error-toned) direction. */
export function publicationRuleDirectionColor(
  direction?: PublicationRuleDirection | string,
): 'success' | 'error' | 'default' {
  if (direction === 'INCLUDE') return 'success';
  if (direction === 'EXCLUDE') return 'error';
  return 'default';
}

export const PUBLICATION_CRITERIA_KIND_OPTIONS: { value: PublicationCriteriaKind; label: string }[] = [
  { value: 'MASTER_CATEGORY', label: 'catalog:options.publicationCriteriaKind.masterCategory' },
  { value: 'ATTRIBUTE_VALUE', label: 'catalog:options.publicationCriteriaKind.attributeValue' },
  { value: 'MANUAL_PRODUCT', label: 'catalog:options.publicationCriteriaKind.manualProduct' },
  { value: 'COMPOSITE_FILTER', label: 'catalog:options.publicationCriteriaKind.compositeFilter' },
];

/** i18next keys — render with `t(PUBLICATION_CRITERIA_KIND_LABELS[kind])`. */
export const PUBLICATION_CRITERIA_KIND_LABELS: Record<string, string> = Object.fromEntries(
  PUBLICATION_CRITERIA_KIND_OPTIONS.map((o) => [o.value, o.label]),
);

/** i18next keys — render with `t(PUBLICATION_RUN_TRIGGER_LABELS[trigger])` (or, better, the
 * tolerant `publicationRunTriggerLabel(t, trigger)` helper below). */
export const PUBLICATION_RUN_TRIGGER_LABELS: Record<string, string> = {
  RULE_CHANGE: 'catalog:options.publicationRunTrigger.ruleChange',
  PRODUCT_EVENT: 'catalog:options.publicationRunTrigger.productEvent',
  SCHEDULE: 'catalog:options.publicationRunTrigger.schedule',
  MANUAL: 'catalog:options.publicationRunTrigger.manual',
  TEMPORAL_ACTIVATION: 'catalog:options.publicationRunTrigger.temporalActivation',
  TEMPORAL_EXPIRY: 'catalog:options.publicationRunTrigger.temporalExpiry',
  LIFECYCLE_CHANGE: 'catalog:options.publicationRunTrigger.lifecycleChange',
  STOCK_EVENT: 'catalog:options.publicationRunTrigger.stockEvent',
  PRICE_EVENT: 'catalog:options.publicationRunTrigger.priceEvent',
};

/** i18next keys — render with `t(PUBLICATION_RUN_STATUS_LABELS[status])`. */
export const PUBLICATION_RUN_STATUS_LABELS: Record<string, string> = {
  RUNNING: 'catalog:options.publicationRunStatus.running',
  SUCCESS: 'catalog:options.publicationRunStatus.success',
  FAILED: 'catalog:options.publicationRunStatus.failed',
  PARTIAL: 'catalog:options.publicationRunStatus.partial',
};

/** Colour bucket for the publication-run status chip. */
export function publicationRunStatusColor(
  status?: PublicationRunStatus | string,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'PARTIAL':
      return 'warning';
    case 'FAILED':
      return 'error';
    case 'RUNNING':
      return 'info';
    default:
      return 'default';
  }
}

/** i18next keys — render with `t(PUBLICATION_RUN_OUTCOME_LABELS[outcome])` (or, better, the
 * tolerant `publicationRunOutcomeLabel(t, outcome)` helper below). */
export const PUBLICATION_RUN_OUTCOME_LABELS: Record<string, string> = {
  EXCLUDED_BY_RULE: 'catalog:options.publicationRunOutcome.excludedByRule',
  FAILED_PROFILE: 'catalog:options.publicationRunOutcome.failedProfile',
  NO_PRIMARY_CATALOG: 'catalog:options.publicationRunOutcome.noPrimaryCatalog',
  CATALOG_TYPE_INACTIVE: 'catalog:options.publicationRunOutcome.catalogTypeInactive',
  NOT_ELIGIBLE_FOR_PUBLICATION: 'catalog:options.publicationRunOutcome.notEligibleForPublication',
};

/** Colour bucket for a run's per-product outcome chip. */
export function publicationRunOutcomeColor(
  outcome?: PublicationRunOutcomeKind | string,
): 'warning' | 'error' | 'default' {
  switch (outcome) {
    case 'EXCLUDED_BY_RULE':
    case 'FAILED_PROFILE':
      return 'warning';
    case 'NO_PRIMARY_CATALOG':
    case 'CATALOG_TYPE_INACTIVE':
      return 'error';
    // Not a rule/profile/config failure — informational, so it gets a neutral chip like the
    // "included" states rather than the warning/error tones above.
    case 'NOT_ELIGIBLE_FOR_PUBLICATION':
    default:
      return 'default';
  }
}

// ── Safe (fallback-to-raw) i18n label lookups for publication-run enums ─────────────────────
// `PublicationRunTrigger`/`PublicationRunStatus`/`PublicationRunOutcomeKind` are versioned
// independently on the backend (web-catalog-publication-service) from this frontend build. A
// value the backend already emits but this build doesn't recognise yet (no entry in the LABELS
// map above) must still render — as the raw enum string — instead of calling `t(undefined)`,
// which throws and used to take the whole panel down via the route's ErrorBoundary. Prefer these
// helpers over indexing *_LABELS directly at any new publication-run render site.

/** Loosely-typed to match react-i18next's `t` without dragging in the full `TFunction` generics (mirrors `ProductPublicationCard`). */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

function safeOptionLabel(t: Translate, labels: Record<string, string>, value: string): string {
  const key = labels[value];
  return key ? t(key) : value;
}

/** `t(PUBLICATION_RUN_TRIGGER_LABELS[trigger])`, tolerant of a trigger this build doesn't recognise yet. */
export function publicationRunTriggerLabel(t: Translate, trigger: string): string {
  return safeOptionLabel(t, PUBLICATION_RUN_TRIGGER_LABELS, trigger);
}

/** `t(PUBLICATION_RUN_STATUS_LABELS[status])`, tolerant of a status this build doesn't recognise yet. */
export function publicationRunStatusLabel(t: Translate, status: string): string {
  return safeOptionLabel(t, PUBLICATION_RUN_STATUS_LABELS, status);
}

/** `t(PUBLICATION_RUN_OUTCOME_LABELS[outcome])`, tolerant of an outcome this build doesn't recognise yet. */
export function publicationRunOutcomeLabel(t: Translate, outcome: string): string {
  return safeOptionLabel(t, PUBLICATION_RUN_OUTCOME_LABELS, outcome);
}

export const PUBLICATION_PROFILE_FIELD_OPTIONS: { value: PublicationProfileFieldKind; label: string }[] = [
  { value: 'code', label: 'catalog:options.publicationProfileField.code' },
  { value: 'name', label: 'catalog:options.publicationProfileField.name' },
  { value: 'category', label: 'catalog:options.publicationProfileField.category' },
  { value: 'brand', label: 'catalog:options.publicationProfileField.brand' },
  { value: 'description', label: 'catalog:options.publicationProfileField.description' },
  { value: 'unit', label: 'catalog:options.publicationProfileField.unit' },
  { value: 'barcode', label: 'catalog:options.publicationProfileField.barcode' },
  { value: 'markingGroup', label: 'catalog:options.publicationProfileField.markingGroup' },
  { value: 'composition', label: 'catalog:options.publicationProfileField.composition' },
];

/** i18next keys — render with `t(PUBLICATION_PROFILE_FIELD_LABELS[field])`. */
export const PUBLICATION_PROFILE_FIELD_LABELS: Record<string, string> = Object.fromEntries(
  PUBLICATION_PROFILE_FIELD_OPTIONS.map((o) => [o.value, o.label]),
);

/** i18next keys — render with `t(PUBLICATION_PROFILE_SOURCE_LABELS[source])`. */
export const PUBLICATION_PROFILE_SOURCE_LABELS: Record<string, string> = {
  PLATFORM_DEFAULT: 'catalog:options.publicationProfileSource.platformDefault',
  TENANT_OVERRIDE: 'catalog:options.publicationProfileSource.tenantOverride',
};

export function publicationProfileSourceColor(
  source?: PublicationProfileSource | string,
): 'primary' | 'default' {
  return source === 'TENANT_OVERRIDE' ? 'primary' : 'default';
}
